import React, { useEffect, useState, lazy, Suspense, Fragment } from 'react';
import './App.css';
import { GlobalProvider } from './context/GlobalState';
import { Routes, Route } from 'react-router-dom';
import { routerUrls, serverUrl } from 'constants';
import Products from './components/Products';
import Home from './components/Home';
import ProductView from './components/ProductView';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import OrderPlaced from './components/pages/OrderPlaced';
import SubmittedForm from 'components/pages/SubmittedForm';
import Layout from './components/Layout';
import FooterBazar from './components/FooterBazar';
import ProductsSearch from 'components/ProductsSearch';
import ProductsCategories from 'components/ProductsCategories';
import PageNotFound from 'components/PageNotFound';
import BackToTopButton from './components/BackToTopButton';
import MessengerButtons from './components/MessengerButtons';
import axios from 'axios';
import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';
import { Link } from 'react-router-dom';
import MetaPixel from './components/MetaPixel';
import CategoryLandingPage from './components/pages/CategoryLandingPage';

const COOKIE_NAME = 'myAwesomeCookieName2';

// Footer pages
const TermsAndConditions = lazy(() => import('./components/pages/TermsAndConditions'));
const ContactForm = lazy(() => import('./components/pages/ContactForm'));
const UsefulInformation = lazy(() => import('./components/pages/UsefulInformation'));
const SizingGuides = lazy(() => import('./components/pages/SizingGuides'));
const ReturnInformation = lazy(() => import('./components/pages/ReturnInformation'));
const ReturnForm = lazy(() => import('./components/pages/ReturnForm'));
const PrivacyPolicy = lazy(() => import('./components/pages/PrivacyPolicy'));
const PaymentMethods = lazy(() => import('./components/pages/PaymentMethods'));
const DeliveryInformation = lazy(() => import('./components/pages/DeliveryInformation'));
const Contacts = lazy(() => import('./components/pages/Contacts'));
const BlogList = lazy(() => import('./components/pages/BlogList'));
const BlogPost = lazy(() => import('./components/pages/BlogPost'));

axios.defaults.baseURL = serverUrl;

// ReactGA.initialize('G-EK216L1EX4');

export default function App() {
  // The consent banner, the back-to-top button and the Viber/WhatsApp column all pin to the
  // bottom of the viewport, and the buttons' higher z-index would swallow taps meant for
  // "Приемам". Hold them back until the banner is gone.
  const [cookieBannerOpen, setCookieBannerOpen] = useState(
    () => getCookieConsentValue(COOKIE_NAME) === undefined
  );

  // Prevent right-click/save on all images
  useEffect(() => {
    const preventImageContextMenu = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', preventImageContextMenu);
    return () => document.removeEventListener('contextmenu', preventImageContextMenu);
  }, []);

  // useEffect(() => {
  //   ReactGA.send(window.location.pathname);
  // }, []);

  // const [gaLoaded, setGaLoaded] = useState(false);

  // useEffect(() => {
  //   if (!gaLoaded) {
  //     import('react-ga4').then((ReactGA) => {
  //       ReactGA.initialize('G-EK216L1EX4');
  //       ReactGA.send(window.location.pathname);
  //       setGaLoaded(true);
  //     });
  //   }
  // }, []);

  return (
    <React.Fragment>
      <GlobalProvider>
        <MetaPixel />

        <div id="container">
          <Layout />
          <CookieConsent
            location="bottom"
            buttonText="Приемам"
            cookieName={COOKIE_NAME}
            onAccept={() => setCookieBannerOpen(false)}
            style={{
              background: '#1D1C1A',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '16px',
              marginRight: '36px'
            }}
            buttonStyle={{
              color: '#1D1C1A',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: '#F57819',
              padding: '8px 20px',
              borderRadius: '2px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '100px',
              flexShrink: 0,
              marginRight: '16px',
              '&:hover': {
                backgroundColor: '#A34D08'
              }
            }}
            expires={365}>
            <div
              style={{
                color: '#ECEBE7',
                textAlign: 'center',
                flex: '1 1 auto',
                minWidth: '280px'
              }}>
              Сайтът използва бисквитки (cookies) за подобряване на неговата ефективност.
              Продължавайки използването му, Вие се съгласявате с нашата {}
              <Link
                to="/deklaratsiq-za-poveritelnost"
                style={{
                  color: '#F57819',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    color: '#A34D08'
                  }
                }}>
                "Политика за употреба на бисквитки"
              </Link>
            </div>
          </CookieConsent>
          <div id="main-content">
            <Routes>
              <Route path={routerUrls.root} element={<Home />} />
              {/* Preview of the new Aurum home design - review before flipping "/" */}

              <Route
                path={routerUrls.adultCategories}
                element={<CategoryLandingPage audience="adult" />}
              />
              <Route
                path={routerUrls.kidsCategories}
                element={<CategoryLandingPage audience="kids" />}
              />
              <Route
                path={routerUrls.cottonSatinCategories}
                element={<CategoryLandingPage audience="cottonSatin" />}
              />

              <Route path={routerUrls.products + '/*'} element={<Products />} />
              <Route
                path={
                  routerUrls.products +
                  '/:category/:subcategory/:categoryId/:subCategoryId/:materialId'
                }
                element={<Products />}
              />
              {/* Mixed page: all materials of a subcategory, sorted by position, with a sticky filter */}
              <Route
                path={routerUrls.products + '/:category/:subcategory/:categoryId/:subCategoryId'}
                element={<Products />}
              />
              <Route
                path={
                  routerUrls.productsCategories +
                  '/:category/:subcategory/:categoryId/:subCategoryId'
                }
                element={<ProductsCategories />}
              />
              <Route path={routerUrls.search + '/:term'} element={<ProductsSearch />} />

              <Route path={routerUrls.product + '/:slug/:id'} element={<ProductView />} />

              <Route path={routerUrls.cart} element={<Cart />} />
              <Route path={routerUrls.checkout} element={<Checkout />} />
              <Route path={routerUrls.orderPlaced} element={<OrderPlaced />} />
              <Route
                path={routerUrls.contacts}
                element={
                  <Suspense fallback={<>...</>}>
                    <Contacts />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.contactForm}
                element={
                  <Suspense fallback={<>...</>}>
                    <ContactForm />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.deliveryInfo}
                element={
                  <Suspense fallback={<>...</>}>
                    <DeliveryInformation />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.paymentMethods}
                element={
                  <Suspense fallback={<>...</>}>
                    <PaymentMethods />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.privacyPolicy}
                element={
                  <Suspense fallback={<>...</>}>
                    <PrivacyPolicy />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.returnForm}
                element={
                  <Suspense fallback={<>...</>}>
                    <ReturnForm />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.returnInfo}
                element={
                  <Suspense fallback={<>...</>}>
                    <ReturnInformation />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.sizingGuides}
                element={
                  <Suspense fallback={<>...</>}>
                    <SizingGuides />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.termsAndConditions}
                element={
                  <Suspense fallback={<>...</>}>
                    <TermsAndConditions />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.usefulInfo}
                element={
                  <Suspense fallback={<>...</>}>
                    <UsefulInformation />
                  </Suspense>
                }
              />
              <Route path={routerUrls.submittedForm} element={<SubmittedForm />} />

              <Route
                path={routerUrls.blog}
                element={
                  <Suspense fallback={<>...</>}>
                    <BlogList />
                  </Suspense>
                }
              />
              <Route
                path={routerUrls.blog + '/:slug'}
                element={
                  <Suspense fallback={<>...</>}>
                    <BlogPost />
                  </Suspense>
                }
              />

              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </div>
          <FooterBazar />
        </div>
        {!cookieBannerOpen && (
          <Fragment>
            <MessengerButtons />
            <BackToTopButton />
          </Fragment>
        )}
      </GlobalProvider>
    </React.Fragment>
  );
}
